import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLenis } from "lenis/react";
import { HERO_IMAGE, CONTACT } from "../data/content";

const LINES = [
  { text: "Strength,", accent: false },
  { text: "Engineered", accent: false },
  { text: "Within Budget.", accent: true },
];

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.3 + i * 0.14, duration: 1.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
});

const Hero = () => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const go = (e, href) => {
    e.preventDefault();
    lenis?.scrollTo(href, { offset: -64, duration: 1.6 });
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen blueprint-grid overflow-hidden pt-16">
      <span className="absolute top-20 left-4 font-mono text-blueprint/50 select-none" aria-hidden>+</span>
      <span className="absolute top-20 right-4 font-mono text-blueprint/50 select-none" aria-hidden>+</span>
      <span className="absolute bottom-6 left-4 font-mono text-blueprint/50 select-none" aria-hidden>+</span>

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center min-h-[calc(100vh-4rem)] py-14">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p
            {...fadeUp(0.15)}
            data-testid="hero-overline"
            className="font-mono text-[10px] md:text-[11px] tracking-[0.35em] uppercase text-blueprint-accent mb-8"
          >
            Sai Mohan Constructions — Gachibowli, Hyderabad — Est. 2007
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-[13.5vw] sm:text-6xl md:text-7xl xl:text-[6.2rem] text-prussian">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  data-testid={`hero-headline-line-${i + 1}`}
                  className={`block ${line.accent ? "text-blueprint-accent" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            {...fadeUp(0.9)}
            data-testid="hero-subtext"
            className="mt-8 max-w-md text-sm md:text-base text-slate-600 leading-relaxed"
          >
            Residential, commercial and turnkey construction across Telangana.
            Structural strength you can bank on — at a cost that makes sense.
          </motion.p>

          <motion.div {...fadeUp(1.05)} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              onClick={(e) => go(e, "#contact")}
              data-testid="hero-cta-quote"
              className="inline-flex items-center gap-2 bg-blueprint-accent text-white font-mono text-xs tracking-[0.15em] uppercase px-7 py-4 hover:bg-prussian transition-colors duration-300"
            >
              Start Your Project <ArrowUpRight size={15} />
            </a>
            <a
              href="#projects"
              onClick={(e) => go(e, "#projects")}
              data-testid="hero-cta-projects"
              className="inline-flex items-center gap-2 border border-prussian text-prussian font-mono text-xs tracking-[0.15em] uppercase px-7 py-4 hover:bg-prussian hover:text-white transition-colors duration-300"
            >
              View Projects
            </a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.5, duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative border border-blueprint-line"
          >
            <div className="overflow-hidden h-[380px] md:h-[520px]">
              <motion.img
                src={HERO_IMAGE}
                alt="Valluramma Nilayam — independent 2-floor 2 BHK apartments by Sai Mohan Constructions"
                data-testid="hero-image"
                style={{ y: imgY, scale: 1.18 }}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-px -left-px bg-white border border-blueprint-line px-4 py-2.5">
              <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-slate-500">
                Fig. 01 — Valluramma Nilayam Elevation
              </p>
            </div>
            <div className="absolute top-4 -right-px bg-blueprint-accent text-white px-3 py-2">
              <p className="font-mono text-[9px] tracking-[0.2em] uppercase">ISO 9001:2015</p>
            </div>
          </motion.div>

          <motion.p
            {...fadeUp(1.2)}
            className="mt-5 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400 flex justify-between"
          >
            <span>17.3850° N, 78.4867° E</span>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-link"
              className="text-blueprint-accent hover:text-prussian transition-colors duration-300"
            >
              WhatsApp Us ↗
            </a>
          </motion.p>
        </div>
      </div>

      <motion.button
        {...fadeUp(1.4)}
        onClick={(e) => go(e, "#stats")}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-slate-400 hover:text-blueprint-accent transition-colors duration-300"
        aria-label="Scroll down"
      >
        <span className="font-mono text-[9px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
